import React, { useState } from "react";
import {
  useListContext,
  useDataProvider,
  useNotify,
  ExportButton,
} from "react-admin";

const escapeCSV = (value: any): string => {
  if (value === null) return "";
  let str = Array.isArray(value) ? value.join(",") : String(value);
  // Check if quoting is necessary
  if (
    str.includes(",") ||
    str.includes("\n") ||
    str.includes("\r") ||
    str.includes('"')
  ) {
    // Escape internal double quotes
    const escapedValue = str.replace(/"/g, '""');
    return `"${escapedValue}"`;
  }
  return str;
};

const toCSV = (data: any[]): string => {
  if (!data.length) return "";
  const keys = Object.keys(data[0]);
  const csvRows = [keys.map(escapeCSV).join(",")];
  for (const row of data) {
    csvRows.push(keys.map((k) => escapeCSV(row[k])).join(","));
  }
  return csvRows.join("\n");
};

const ExportAllButton: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { resource, filterValues } = useListContext();

  const dataProvider = useDataProvider();
  const notify = useNotify();

  const handleExport = async () => {
    setLoading(true);
    try {
      if (!resource) {
        throw new Error("Resource name is required for export");
      }

      const { data } = await dataProvider.getAll(resource, {
        filter: filterValues,
      });
      // Always read the latest selected columns from localStorage at export time
      const columnsKey = `RaStore.preferences.${resource}.datagrid.columns`;
      const availableKey = `RaStore.preferences.${resource}.datagrid.availableColumns`;

      let selectedColumns: string[] | undefined = undefined;
      const columnsRaw = localStorage.getItem(columnsKey);
      const availableRaw = localStorage.getItem(availableKey);

      if (columnsRaw && availableRaw) {
        try {
          const selectedIndexes: string[] = JSON.parse(columnsRaw);
          const availableColumns: { index: string; source: string }[] =
            JSON.parse(availableRaw);
          selectedColumns = selectedIndexes
            .map(
              (idx) => availableColumns.find((col) => col.index === idx)?.source
            )
            .filter((src): src is string => !!src);
        } catch {
          selectedColumns = undefined;
        }
      }
      let filteredData = data;
      let csv = "";
      // Always filter out '_id' and '__v' columns
      const filterOutKeys = ["_id", "__v"];
      if (selectedColumns && selectedColumns.length > 0) {
        // Remove unwanted keys from selectedColumns
        const filteredColumns = selectedColumns.filter(
          (col) => !filterOutKeys.includes(col)
        );
        // For each row, ensure all filteredColumns are present (empty if missing)
        filteredData = data.map((row: any) => {
          const newRow: Record<string, any> = {};
          filteredColumns.forEach((col) => {
            newRow[col] = row.hasOwnProperty(col) ? row[col] : "";
          });
          return newRow;
        });
        csv = toCSV(filteredData);
      } else {
        // Remove unwanted keys from all rows
        filteredData = data.map((row: any) => {
          const newRow: Record<string, any> = {};
          Object.keys(row).forEach((key) => {
            if (!filterOutKeys.includes(key)) {
              newRow[key] = row[key];
            }
          });
          return newRow;
        });
        csv = toCSV(filteredData);
      }
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
      const fileName = `${resource}-${dateStr}.csv`;
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify("Export complete", {
        type: "success",
        autoHideDuration: 5000,
        container: "center",
      });
    } catch (e) {
      notify("Export failed", {
        type: "error",
        autoHideDuration: 10000,
        container: "center",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExportButton
      disabled={loading}
      exporter={handleExport}
      label={loading ? "Exporting..." : "Export"}
    />
  );
};

export default ExportAllButton;
